import styles from "./styles.module.scss";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputBase,
  Typography,
} from "@mui/material";
import { useState } from "react";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import axios from "axios";
import { useNotify, useReduxUser, useReduxUserProfile } from "Utils/hooks";
import { getProfile } from "Redux/Actions";
import { baseURL, getToken } from "Shared/baseURL";
import { attachApiInterceptors } from "Shared/apiInterceptors";
import { useDispatch } from "react-redux";

const resolveUserId = (user) => user?.id || user?.userId || user?.uuid || null;
const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
const PUBLIC_NEWSLETTER_SUBSCRIPTION_PATH =
  "/v1/public-newsletters/subscriptions";
const rem = (px) => `${px / 16}rem`;
const resolvePublicNewsletterBase = () => {
  const normalizedBase = String(baseURL || "").replace(/\/$/, "");
  return normalizedBase && normalizedBase !== "/api"
    ? `${normalizedBase}/ncs`
    : "/ncs";
};

const publicNewsletterClient = axios.create({
  baseURL: resolvePublicNewsletterBase(),
  withCredentials: false,
});
attachApiInterceptors(publicNewsletterClient, "publicNewsletterClient");

publicNewsletterClient.interceptors.request.use((config) => {
  if (config.headers) {
    delete config.headers.Authorization;
  }
  return config;
});

const subscribePublicNewsletter = (email) =>
  publicNewsletterClient.post(PUBLIC_NEWSLETTER_SUBSCRIPTION_PATH, { email });

const Subscribe = ({ isFooter = false, buttonLabel = "Subscribe" }) => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [subscribedOverride, setSubscribedOverride] = useState(false);
  const [
    newsletterPreferencePromptOpen,
    setNewsletterPreferencePromptOpen,
  ] = useState(false);
  const notify = useNotify();
  const dispatch = useDispatch();
  const profile = useReduxUserProfile();
  const reduxUser = useReduxUser();
  const currentUserId = resolveUserId(profile || reduxUser);
  const showFooterSubscribedState = isFooter && subscribedOverride;

  const handleSubscribe = async (e) => {
    e.preventDefault();
    const trimmedEmail = email.trim();

    if ((!currentUserId || isFooter) && !isValidEmail(trimmedEmail)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    setEmailError("");

    setSubmitting(true);
    try {
      if (isFooter) {
        await subscribePublicNewsletter(trimmedEmail);
      } else if (currentUserId) {
        const token = await getToken();
        await axios.patch(
          `/sns/v1/users/${currentUserId}`,
          { newsletterSubscribed: true },
          {
            headers: {
              Authorization: token,
              "Content-Type": "application/json",
            },
          },
        );
        dispatch(getProfile());
      } else {
        await subscribePublicNewsletter(trimmedEmail);
      }
      notify("Subscribed to newsletter");
      setEmail("");
      setSubscribedOverride(true);
      if (isFooter && currentUserId) {
        setNewsletterPreferencePromptOpen(true);
      }
    } catch (error) {
      if (error?.response?.data?.message === "USER_ALREADY_SIGNED_UP") {
        if (isFooter) {
          notify("This email is already subscribed to our newsletter.", {
            variant: "default",
          });
          setEmail("");
          setSubscribedOverride(true);
          if (currentUserId) {
            setNewsletterPreferencePromptOpen(true);
          }
        } else {
          notify(
            "It looks like you are already subscribed to our newsletters.",
            {
              variant: "default",
            },
          );
        }
      } else {
        const publicEndpointUnavailable = [401, 404].includes(
          error?.response?.status,
        );
        const fallbackMessage =
          publicEndpointUnavailable
            ? "Newsletter signup is not available right now. Please try again later."
            : error?.response?.data?.message ||
              error.message ||
              "Subscription failed";

        if (isFooter) {
          setEmailError(fallbackMessage);
        } else {
          notify(fallbackMessage, { variant: "error" });
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleFooterNewsletterPreference = async (checked) => {
    setNewsletterPreferencePromptOpen(false);

    if (!currentUserId) {
      return;
    }

    setSubmitting(true);
    try {
      const token = await getToken();
      await axios.patch(
        `/sns/v1/users/${currentUserId}`,
        { newsletterSubscribed: checked },
        {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        },
      );
      dispatch(getProfile());
      notify(
        checked
          ? "Newsletter toggle turned on in Settings."
          : "Newsletter toggle turned off in Settings.",
        { variant: "success" },
      );
    } catch (error) {
      notify(
        error?.response?.data?.message ||
          error?.message ||
          "Could not update newsletter settings.",
        { variant: "error" },
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.main}>
      {showFooterSubscribedState ? (
        <div className={styles.footerSubscribedCard}>
          <CheckCircleOutlineIcon className={styles.footerSubscribedIcon} />
          <div>
            <p className={styles.footerSubscribedTitle}>
              You&apos;re subscribed
            </p>
            <p className={styles.footerSubscribedText}>
              Thanks for signing up. You&apos;ll receive regular updates from
              Kewordal in your inbox.
            </p>
          </div>
        </div>
      ) : null}
      {isFooter && !showFooterSubscribedState ? (
        <p className="footer-newsletter-text">
          Subscribe to our newsletter for regular updates
        </p>
      ) : null}
      {!showFooterSubscribedState ? (
        <Box
          sx={{
            ...(isFooter ? {} : { display: "flex", justifyContent: "center" }),
            mt: isFooter ? 0 : 1.5,
          }}
        >
          <Box
            component="form"
            onSubmit={handleSubscribe}
            sx={{
              display: "flex",
              flexDirection: isFooter ? "column" : "row",
              alignItems: isFooter ? "flex-start" : "center",
              gap: isFooter ? rem(10) : 0,
              width: "100%",
              maxWidth: isFooter ? "100%" : rem(400),
              height: "auto",
              border: isFooter ? "none" : "2px solid #09aff4",
              borderRadius: isFooter ? 0 : rem(16),
              overflow: isFooter ? "visible" : "hidden",
              bgcolor: isFooter ? "transparent" : "#fff",
              boxSizing: "border-box",
            }}
          >
            <InputBase
              id="newsletter-subscribe-email"
              type="email"
              placeholder={isFooter ? "example@mail.com" : "Enter your email.."}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) {
                  setEmailError("");
                }
              }}
              sx={{
                flex: 1,
                px: rem(16),
                pr: isFooter ? rem(16) : rem(8),
                py: isFooter ? 0 : 0.2,
                width: isFooter ? rem(280) : "auto",
                maxWidth: isFooter ? "100%" : "none",
                height: isFooter ? rem(38) : rem(44),
                border: isFooter
                  ? `1px solid ${emailError ? "#ff5a5f" : "#d9d9d9"}`
                  : "none",
                borderRadius: isFooter ? rem(10) : 0,
                bgcolor: "#ffffff",
                fontSize: isFooter ? rem(14) : "0.95rem",
                lineHeight: isFooter ? rem(22) : rem(44),
                color: "#333333",
                boxSizing: "border-box",
              }}
              inputProps={{
                "aria-label": "email for newsletter",
                required: true,
              }}
            />
            <Button
              type="submit"
              variant="contained"
              disableElevation
              disableRipple={isFooter}
              disableFocusRipple={isFooter}
              disabled={submitting}
              sx={{
                m: 0,
                mt: isFooter ? 0 : 0,
                height: isFooter
                  ? `clamp(${rem(38)}, 2.8vw, ${rem(42)})`
                  : rem(44),
                lineHeight: isFooter ? 1 : rem(44),
                borderRadius: isFooter ? "999px" : 0,
                px: isFooter
                  ? `clamp(${rem(14)}, 1vw, ${rem(18)})`
                  : rem(20),
                minWidth: isFooter
                  ? `clamp(${rem(136)}, 10vw, ${rem(154)})`
                  : rem(108),
                fontWeight: 700,
                fontSize: isFooter
                  ? `clamp(${rem(13)}, 0.9vw, ${rem(15)})`
                  : "0.95rem",
                textTransform: "none",
                bgcolor: "#09aff4",
                transition: "background-color 0.2s ease",
                ":hover": {
                  bgcolor: "#09a7ea",
                  boxShadow: isFooter
                    ? "none"
                    : `0 ${rem(4)} ${rem(10)} rgba(0, 0, 0, 0.24)`,
                },
                ":focus": {
                  boxShadow: "none",
                },
                "& .MuiTouchRipple-root": {
                  display: isFooter ? "none" : undefined,
                },
                ":disabled": {
                  bgcolor: "#9bdcf8",
                  color: "#ffffff",
                },
                boxShadow: isFooter
                  ? "none"
                  : `0 ${rem(3)} ${rem(8)} rgba(0, 0, 0, 0.22)`,
              }}
            >
              {submitting ? "Submitting..." : buttonLabel}
            </Button>
          </Box>
          {isFooter && emailError ? (
            <div className={styles.footerNewsletterError} role="alert">
              <WarningAmberIcon className={styles.footerNewsletterErrorIcon} />
              <span>{emailError}</span>
            </div>
          ) : null}
        </Box>
      ) : null}
      <Dialog
        open={newsletterPreferencePromptOpen}
        onClose={() => handleFooterNewsletterPreference(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Turn on newsletter updates?</DialogTitle>
        <DialogContent>
          <Typography>
            Should we turn on the Newsletter toggle in your Settings page?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            type="button"
            onClick={() => handleFooterNewsletterPreference(false)}
          >
            No
          </Button>
          <Button
            type="button"
            variant="contained"
            onClick={() => handleFooterNewsletterPreference(true)}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Subscribe;
